import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  acceptCourseRecordingTerms,
  createCourseRecording,
  deleteCourseRecording,
  fetchCourseRecordings,
  updateCourseRecording,
} from "../../services/recordingsApi";
import "./CourseRecordingsPanel.css";

const TERMS_TEXT =
  "Las grabaciones del curso son material de uso exclusivamente académico. No deben compartirse, redistribuirse, publicarse ni ponerse a disposición de personas ajenas al curso. El acceso está sujeto a la vigencia del curso y a las condiciones definidas por Grupo Derecho y Progreso.";

const emptyForm = {
  titulo: "",
  descripcion: "",
  fecha_sesion: "",
  proveedor: "zoom",
  url: "",
  codigo_acceso: "",
  visible_hasta: "",
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const padDatePart = (value) => String(value).padStart(2, "0");

const formatDate = (value) => {
  if (!value) {
    return "Sin fecha";
  }

  const raw = String(value).trim();
  const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoDate) {
    const date = new Date(
      Number(isoDate[1]),
      Number(isoDate[2]) - 1,
      Number(isoDate[3]),
      12,
      0,
      0,
    );

    return new Intl.DateTimeFormat("es-MX", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return raw;
  }

  return new Intl.DateTimeFormat("es-MX", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

const normalizeDateTimeValue = (value) => {
  if (!value) {
    return "";
  }

  const raw = String(value).trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  if (match) {
    return `${match[1]}T${match[2]}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  return `${parsed.getUTCFullYear()}-${padDatePart(
    parsed.getUTCMonth() + 1,
  )}-${padDatePart(parsed.getUTCDate())}T${padDatePart(
    parsed.getUTCHours(),
  )}:${padDatePart(parsed.getUTCMinutes())}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return "Sin límite definido";
  }

  const normalized = normalizeDateTimeValue(value);
  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/,
  );

  if (!match) {
    return String(value);
  }

  const date = new Date(
    Date.UTC(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
    ),
  );

  return new Intl.DateTimeFormat("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(date);
};

const CourseRecordingsPanel = ({ mode = "readonly", idClase, accessToken }) => {
  const isAdmin = mode === "admin";
  const isStudent = mode === "student";
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [courseExpired, setCourseExpired] = useState(false);
  const [termsRequired, setTermsRequired] = useState(false);
  const [termsVersion, setTermsVersion] = useState("1.0");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [actionMessage, setActionMessage] = useState("");

  const canLoad = Boolean(idClase && accessToken);

  const loadRecordings = useCallback(async () => {
    if (!canLoad) {
      setRecordings([]);
      return;
    }

    setLoading(true);
    setError("");
    setCourseExpired(false);

    try {
      const response = await fetchCourseRecordings({
        idClase,
        accessToken,
        admin: isAdmin,
      });
      setRecordings(Array.isArray(response.data?.data) ? response.data.data : []);
      setTermsRequired(false);
      setTermsVersion(response.data?.terms_version || "1.0");
      setCourseExpired(response.data?.code === "COURSE_EXPIRED");
    } catch (requestError) {
      const code = requestError?.response?.data?.code;
      if (isStudent && code === "TERMS_REQUIRED") {
        setRecordings([]);
        setTermsRequired(true);
        setTermsVersion(requestError.response.data?.terms_version || "1.0");
      } else if (code === "COURSE_EXPIRED") {
        setRecordings([]);
        setCourseExpired(true);
      } else {
        setRecordings([]);
        setError(
          getErrorMessage(
            requestError,
            "No fue posible cargar las grabaciones del curso.",
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [accessToken, canLoad, idClase, isAdmin, isStudent]);

  useEffect(() => {
    setActionMessage("");
    setAcceptedTerms(false);
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    loadRecordings();
  }, [idClase, accessToken, loadRecordings]);

  const sortedRecordings = useMemo(
    () =>
      [...recordings].sort((a, b) =>
        String(b.fecha_sesion || "").localeCompare(String(a.fecha_sesion || "")),
      ),
    [recordings],
  );

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!idClase || !accessToken) {
      return;
    }

    setSaving(true);
    setError("");
    setActionMessage("");

    const payload = {
      titulo: form.titulo.trim(),
      descripcion: form.descripcion.trim() || null,
      fecha_sesion: form.fecha_sesion,
      proveedor: form.proveedor,
      url: form.url.trim(),
      codigo_acceso: form.codigo_acceso.trim() || null,
      visible_hasta: form.visible_hasta || null,
    };

    try {
      const successMessage = editingId
        ? "Grabación actualizada correctamente."
        : "Grabación agregada correctamente.";

      if (editingId) {
        await updateCourseRecording({
          idGrabacion: editingId,
          data: payload,
          accessToken,
        });
      } else {
        await createCourseRecording({
          data: { ...payload, id_clase: idClase },
          accessToken,
        });
      }

      resetForm();
      await loadRecordings();
      setActionMessage(successMessage);
    } catch (requestError) {
      setError(
        getErrorMessage(requestError, "No fue posible guardar la grabación."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (recording) => {
    setActionMessage("");
    setEditingId(recording.id_grabacion);
    setForm({
      titulo: recording.titulo || "",
      descripcion: recording.descripcion || "",
      fecha_sesion: String(recording.fecha_sesion || "").slice(0, 10),
      proveedor: recording.proveedor || "zoom",
      url: recording.url || "",
      codigo_acceso: recording.codigo_acceso || "",
      visible_hasta: normalizeDateTimeValue(recording.visible_hasta),
    });
    setShowForm(true);
  };

  const handleToggleActive = async (recording) => {
    setError("");
    setActionMessage("");

    try {
      await updateCourseRecording({
        idGrabacion: recording.id_grabacion,
        data: { activo: !Boolean(recording.activo) },
        accessToken,
      });
      await loadRecordings();
      setActionMessage(
        recording.activo
          ? "Grabación desactivada correctamente."
          : "Grabación activada correctamente.",
      );
    } catch (requestError) {
      setError(
        getErrorMessage(
          requestError,
          "No fue posible cambiar el estado de la grabación.",
        ),
      );
    }
  };

  const handleDelete = async (recording) => {
    const confirmed = window.confirm(
      `¿Eliminar la grabación “${recording.titulo || "Sin título"}”?`,
    );
    if (!confirmed) {
      return;
    }

    setError("");
    setActionMessage("");

    try {
      await deleteCourseRecording({
        idGrabacion: recording.id_grabacion,
        accessToken,
      });
      await loadRecordings();
      setActionMessage("Grabación eliminada correctamente.");
    } catch (requestError) {
      setError(
        getErrorMessage(requestError, "No fue posible eliminar la grabación."),
      );
    }
  };

  const handleAcceptTerms = async () => {
    if (!acceptedTerms) {
      return;
    }

    setAcceptingTerms(true);
    setError("");
    try {
      await acceptCourseRecordingTerms({
        idClase,
        termsVersion,
        accessToken,
      });
      setTermsRequired(false);
      setAcceptedTerms(false);
      await loadRecordings();
    } catch (requestError) {
      setError(
        getErrorMessage(
          requestError,
          "No fue posible registrar la aceptación de términos.",
        ),
      );
    } finally {
      setAcceptingTerms(false);
    }
  };

  if (!idClase) {
    return (
      <section className="course-recordings-panel">
        <div className="course-recordings-empty">
          <strong>Selecciona un curso</strong>
          <p>Abre primero una materia para consultar sus grabaciones.</p>
        </div>
      </section>
    );
  }

  if (!accessToken) {
    return (
      <section className="course-recordings-panel">
        <div className="course-recordings-empty course-recordings-error">
          <strong>Sesión de grabaciones no disponible</strong>
          <p>Vuelve a iniciar sesión para consultar esta sección.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="course-recordings-panel">
      <div className="course-recordings-header">
        <div>
          <span className="course-recordings-eyebrow">Clases grabadas</span>
          <h2>Grabaciones del curso</h2>
          <p>
            Las grabaciones se abren desde el proveedor donde fueron alojadas.
          </p>
        </div>

        {isAdmin && (
          <button
            className="course-recordings-primary"
            type="button"
            onClick={() => {
              setActionMessage("");
              if (showForm) {
                resetForm();
              } else {
                setShowForm(true);
              }
            }}
          >
            {showForm ? "Cancelar" : "+ Agregar grabación"}
          </button>
        )}
      </div>

      {isAdmin && showForm && (
        <form className="course-recordings-form" onSubmit={handleSubmit}>
          <div className="course-recordings-form-heading">
            <h3>{editingId ? "Editar grabación" : "Agregar grabación"}</h3>
            <p>
              Si no defines una vigencia diferente, se tomará como fecha base las
              15:00 del día siguiente al fin del curso, hora de Ciudad de México.
              El acceso permanecerá disponible 7 días adicionales después de esa fecha.
            </p>
          </div>

          <div className="course-recordings-form-grid">
            <label>
              <span>Título</span>
              <input
                name="titulo"
                value={form.titulo}
                onChange={handleFormChange}
                placeholder="Sesión 1 - Introducción"
                maxLength={200}
                required
              />
            </label>

            <label>
              <span>Fecha de sesión</span>
              <input
                name="fecha_sesion"
                type="date"
                value={form.fecha_sesion}
                onChange={handleFormChange}
                required
              />
            </label>

            <label>
              <span>Proveedor</span>
              <select
                name="proveedor"
                value={form.proveedor}
                onChange={handleFormChange}
              >
                <option value="zoom">Zoom</option>
                <option value="drive">Google Drive</option>
              </select>
            </label>

            <label>
              <span>Código de acceso</span>
              <input
                name="codigo_acceso"
                value={form.codigo_acceso}
                onChange={handleFormChange}
                placeholder="Opcional"
                maxLength={100}
              />
            </label>

            <label className="course-recordings-form-wide">
              <span>Enlace de grabación</span>
              <input
                name="url"
                type="url"
                value={form.url}
                onChange={handleFormChange}
                placeholder={
                  form.proveedor === "zoom"
                    ? "https://...zoom.us/rec/share/..."
                    : "https://drive.google.com/..."
                }
                required
              />
            </label>

            <label>
              <span>Fecha de vencimiento base (hora CDMX)</span>
              <input
                name="visible_hasta"
                type="datetime-local"
                value={form.visible_hasta}
                onChange={handleFormChange}
              />
            </label>

            <label className="course-recordings-form-wide">
              <span>Descripción</span>
              <textarea
                name="descripcion"
                value={form.descripcion}
                onChange={handleFormChange}
                rows={3}
                placeholder="Opcional"
              />
            </label>
          </div>

          <div className="course-recordings-form-actions">
            <button
              className="course-recordings-primary"
              type="submit"
              disabled={saving}
            >
              {saving
                ? "Guardando..."
                : editingId
                  ? "Guardar cambios"
                  : "Agregar grabación"}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} disabled={saving}>
                Cancelar edición
              </button>
            )}
          </div>
        </form>
      )}

      {actionMessage && (
        <div className="course-recordings-message course-recordings-success">
          {actionMessage}
        </div>
      )}

      {error && (
        <div className="course-recordings-message course-recordings-error">
          {error}
        </div>
      )}

      {termsRequired && (
        <div className="course-recordings-terms" role="dialog" aria-modal="true">
          <div className="course-recordings-terms-card">
            <span className="course-recordings-eyebrow">Acceso a grabaciones</span>
            <h3>Términos de uso</h3>
            <p>{TERMS_TEXT}</p>
            <label className="course-recordings-check">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(event) => setAcceptedTerms(event.target.checked)}
              />
              <span>He leído y acepto los términos de uso.</span>
            </label>
            <button
              className="course-recordings-primary"
              type="button"
              disabled={!acceptedTerms || acceptingTerms}
              onClick={handleAcceptTerms}
            >
              {acceptingTerms ? "Registrando..." : "Aceptar y continuar"}
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="course-recordings-empty">
          <strong>Cargando grabaciones...</strong>
          <p>Consultando las sesiones disponibles para este curso.</p>
        </div>
      )}

      {!loading && courseExpired && (
        <div className="course-recordings-empty">
          <strong>Periodo de grabaciones finalizado</strong>
          <p>Las grabaciones de este curso ya no están disponibles.</p>
        </div>
      )}

      {!loading && !courseExpired && !termsRequired && sortedRecordings.length === 0 && (
        <div className="course-recordings-empty">
          <strong>No hay grabaciones disponibles</strong>
          <p>Cuando se registre una sesión aparecerá en esta sección.</p>
        </div>
      )}

      {!loading && !courseExpired && !termsRequired && sortedRecordings.length > 0 && (
        <div className="course-recordings-grid">
          {sortedRecordings.map((recording) => (
            <article
              className={`course-recording-card${recording.activo ? "" : " course-recording-card-inactive"}`}
              key={recording.id_grabacion}
            >
              <div className="course-recording-card-top">
                <div>
                  <span className="course-recording-provider">
                    {recording.proveedor === "drive" ? "Google Drive" : "Zoom"}
                  </span>
                  <h3>{recording.titulo || "Grabación sin título"}</h3>
                </div>
                {isAdmin && (
                  <span className="course-recording-status">
                    {recording.activo ? "Activa" : "Inactiva"}
                  </span>
                )}
              </div>

              {recording.descripcion && <p>{recording.descripcion}</p>}

              <dl className="course-recording-meta">
                <div>
                  <dt>Sesión</dt>
                  <dd>{formatDate(recording.fecha_sesion)}</dd>
                </div>
                <div>
                  <dt>Disponible hasta (vigencia final, hora CDMX)</dt>
                  <dd>
                    {formatDateTime(
                      recording.visible_hasta_efectiva || recording.visible_hasta,
                    )}
                  </dd>
                </div>
              </dl>

              {recording.codigo_acceso && (
                <div className="course-recording-access-code">
                  Código de acceso: <strong>{recording.codigo_acceso}</strong>
                </div>
              )}

              <div className="course-recording-actions">
                <a
                  className="course-recordings-primary"
                  href={recording.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ver grabación
                </a>

                {isAdmin && (
                  <>
                    <button type="button" onClick={() => handleEdit(recording)}>
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleActive(recording)}
                    >
                      {recording.activo ? "Desactivar" : "Activar"}
                    </button>
                    <button
                      className="course-recording-delete"
                      type="button"
                      onClick={() => handleDelete(recording)}
                    >
                      Eliminar
                    </button>
                  </>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default CourseRecordingsPanel;
